import { FC, useEffect, useState } from 'react'
import {
  BooleanField, Datagrid, DateField, DeleteWithConfirmButton, EditButton,
  FunctionField, NumberField, Show, SimpleShowLayout, TabbedShowLayout,
  TextField, TopToolbar, useRecordContext, useGetList, useList,
  ListContextProvider, Pagination, useTranslate, useNotify, Tab, Button,
} from 'react-admin'
import { Box, Typography, Card, CardContent, Chip } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { SeverityChip } from '../../components'
import { getJson, getUrl, postJson } from '../../providers/data'
import { xxllncColor } from '../../layout/theme/color'
import { AutomationRuleOut, MonitoringAlertOut } from '../../types/apiRequests'
import { Registry, TriggerDef, ActionDef } from './wizard/types'


type ItemDef = TriggerDef | ActionDef


interface ConfigItem { type: string; params: Record<string, unknown> }

const ItemSummary: FC<{ config: ConfigItem; definitions: ItemDef[] }> = ({ config, definitions }) => {
  const defn = definitions.find((d) => d.type === config.type)
  if (!defn) return <Typography variant="body2">{config.type}</Typography>

  let text = defn.template
  // eslint-disable-next-line security/detect-object-injection
  for (const [key, val] of Object.entries(config.params)) {
    // eslint-disable-next-line security/detect-object-injection
    const schema = defn.params_schema[key]
    if (schema?.type === 'select' && schema.options) {
      const option = schema.options.find((o) => o.value === val)
      text = text.replace(`{${key}}`, option?.label ?? String(val))
    } else {
      text = text.replace(`{${key}}`, String(val ?? ''))
    }
  }

  return <Typography variant="body2">{text}</Typography>
}


const ShowActions: FC = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const record = useRecordContext<AutomationRuleOut>()

  const handleTest = async () => {
    if (!record) return
    try {
      const result = await postJson<{ message: string }>(
        `${getUrl()}/automation_rule/${record.id}/test`,
      )
      notify(result.message, { type: 'success' })
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    }
  }

  return (
    <TopToolbar>
      <Button label={translate('automationRule.test')} onClick={() => void handleTest()}>
        <PlayArrowIcon />
      </Button>
      <EditButton />
      <DeleteWithConfirmButton
        confirmTitle={translate('delete.confirmTitle')}
        confirmContent={translate('delete.confirmContentMonitoringRule')}
      />
    </TopToolbar>
  )
}


const DetailsTab: FC = () => {
  const record = useRecordContext<AutomationRuleOut>()
  const translate = useTranslate()

  // eslint-disable-next-line camelcase
  const SCOPE_LABELS: Record<string, string> = {
    global: translate('automationRule.scopeGlobal'),
    // eslint-disable-next-line camelcase
    specific_organizations: translate('automationRule.scopeSpecific'),
  }
  const [registry, setRegistry] = useState<Registry | null>(null)

  useEffect(() => {
    const fetch = async () => {
      const result = await getJson<Registry>(`${getUrl()}/automation_rule/registry`)
      setRegistry(result)
    }
    void fetch()
  }, [])

  if (!record) return null

  const triggerDefs = registry?.triggers.categories.flatMap((c) => c.triggers ?? []) ?? []
  const actionDefs = registry?.actions.categories.flatMap((c) => c.actions ?? []) ?? []

  const triggers = record.triggers ?? []
  const actions = record.actions ?? []

  return (
    <SimpleShowLayout>
      <TextField source="display_name" label="data.displayName" />
      <TextField source="description" label="data.description" />
      <FunctionField<AutomationRuleOut>
        label="automationRule.scope"
        render={(rec) =>
          // eslint-disable-next-line security/detect-object-injection
          SCOPE_LABELS[rec.scope] ?? rec.scope
        }
      />
      <FunctionField<AutomationRuleOut>
        label="automationRule.tiers"
        render={(rec) => {
          const tiers = rec.scope_tiers ?? []
          if (tiers.length === 0) return null
          return tiers.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(', ')
        }}
      />
      <FunctionField<AutomationRuleOut>
        label="automationRule.organizations"
        render={(rec) => {
          const orgs = rec.organizations ?? []
          if (orgs.length === 0) return null
          return orgs.map((o) => o.display_name).join(', ')
        }}
      />
      <NumberField source="cooldown_minutes" label="data.cooldownMinutes" />
      <BooleanField source="enabled" label="data.enabled" />
      <DateField source="last_triggered" showTime label="data.lastTriggered" />
      <DateField source="created" showTime label="data.createdAt" />
      <DateField source="lastModified" showTime label="data.lastModified" />

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>{translate('automationRule.triggers')}</Typography>
        {triggers.map((t, i) => (
          <Card key={i} variant="outlined" sx={{ mb: 1, borderRadius: '8px', bgcolor: xxllncColor.opDark10 }}>
            <CardContent sx={{ 'py': 1, 'px': 2, '&:last-child': { pb: 1 } }}>
              <ItemSummary config={t as ConfigItem} definitions={triggerDefs} />
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>{translate('automationRule.actions')}</Typography>
        {actions.map((a, i) => (
          <Card key={i} variant="outlined" sx={{ mb: 1, borderRadius: '8px', bgcolor: xxllncColor.opGreen10 }}>
            <CardContent sx={{ 'py': 1, 'px': 2, '&:last-child': { pb: 1 } }}>
              <ItemSummary config={a as ConfigItem} definitions={actionDefs} />
            </CardContent>
          </Card>
        ))}
      </Box>
    </SimpleShowLayout>
  )
}


const AlertsTab: FC = () => {
  const record = useRecordContext<AutomationRuleOut>()
  const translate = useTranslate()

  const { data, isLoading } = useGetList<MonitoringAlertOut>(
    'monitoring_alert',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'created', order: 'DESC' },
      // eslint-disable-next-line camelcase
      filter: { rule_id: record?.id },
    },
    { enabled: !!record?.id }
  )

  const listContext = useList({ data: data ?? [], isLoading, perPage: 25 })

  return (
    <ListContextProvider value={listContext}>
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <FunctionField
          label="data.severity"
          render={(rec: MonitoringAlertOut) => <SeverityChip severity={rec.severity} />}
        />
        <TextField source="message" />
        <TextField source="organization_display_name" label="data.organization" />
        <FunctionField
          label="data.acknowledged"
          render={(rec: MonitoringAlertOut) =>
            rec.acknowledged ? <CheckCircleIcon fontSize="small" sx={{ color: xxllncColor.green100 }} /> : null
          }
        />
        <DateField source="created" showTime label="data.createdAt" />
      </Datagrid>
      <Pagination />
    </ListContextProvider>
  )
}


const AutomationRuleShow: FC = () => (
  <Show actions={<ShowActions />}>
    <TabbedShowLayout>
      <Tab label="automationRule.tabs.details">
        <DetailsTab />
      </Tab>
      <Tab label="automationRule.tabs.alerts" path="alerts">
        <AlertsTab />
      </Tab>
    </TabbedShowLayout>
  </Show>
)

export default AutomationRuleShow
